// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/params.js — LES LEVIERS DES RÉGLAGES GÉNÉRAUX (Paramètres).
//
// Famille ouverte le 2026-09-14, sur le modèle de `lib/leviers/logements.js`.
// Périmètre reçu : « un réglage général (Paramètres) » — premier lot
// volontairement petit et défendable.
//
// ⚠️ UN SEUL LEVIER POUR SIX RÉGLAGES, ET C'EST VOULU. Le découpage en deux
// leviers de `logement_champ_set` (fiche / commission) existe parce qu'un
// MARQUAGE est porté par un levier entier : la commission est de l'argent, un
// code de porte n'en est pas. Ici, les six réglages du lot sont de la MÊME
// nature — des mentions d'identité qui figurent sur des documents. Rien à
// partitionner : un seul levier, un seul marquage (aucun).
//
// ⚠️ ET CE QUI N'Y EST PAS EST CE QUI COMPTE. `cfg.co` porte aussi
// `claudeKey` (une clé d'API), `iban`, `bic`, `siret`, `tva`/`tauxTva` (le
// montant de chaque facture) et `lastNum` (la numérotation légale). Aucun n'est
// dans le catalogue, et ce n'est pas une consigne : c'est une VÉRIFICATION AU
// CHARGEMENT (`lib/reglages-generaux-catalogue.js::verifierCatalogue`) qui fait
// lever le module si un chemin porte un de ces mots. Un test le prouve sur un
// catalogue gonflé, sans toucher au vrai.

const LEVIERS_PARAMS = Object.freeze({
  // ══ « MODIFIE CE RÉGLAGE GÉNÉRAL » (2026-09-14) ══════════════════════════
  //
  // Action `reglage_general_set` (lib/reglage-general-handlers.js), écrite pour
  // l'occasion sur le patron de `logement_champ_set` : admin seul, jeton
  // `x-cfg-version` obligatoire, relecture juste avant d'écrire, diff intégral,
  // catalogue fermé.
  //
  // ⚠️ VAGUE 9, ET C'EST LA RÈGLE DU DÉPÔT APPLIQUÉE À LA LETTRE : « une action
  // qui demande un texte que Dimitri doit écrire n'est PAS écartée : elle est
  // mise en DERNIÈRE VAGUE ». Un nom de société, une adresse, un e-mail de
  // contact ne sont pas des identifiants observés — ce sont des textes qu'il
  // dicte. Et ils ne restent pas chez nous : le nom signe les e-mails envoyés
  // aux voyageurs, l'adresse et le téléphone figurent sur les factures.
  //
  // ⚠️ `nouvelle` EST LIBRE PAR CONSTRUCTION, et déclaré tel — comme sur
  // `logement_champ_set`, et pas nommé `valeur` : la sentinelle des arguments
  // d'argent ancre `eur$`, et « valeur » y tombait alors qu'un nom de société
  // n'est pas un montant. Le serveur la valide contre le catalogue (forme,
  // longueur, e-mail, téléphone) et refuse tout ce qui n'y entre pas — jamais
  // de troncature, jamais de correction silencieuse.
  //
  // ⚠️ AUCUN DE CES RÉGLAGES NE SE VIDE, et le serveur le refuse plutôt que de
  // l'accepter : un nom de société vide casserait la signature de tous les
  // e-mails, et un médiateur à moitié rempli fait DISPARAÎTRE l'article 16 du
  // contrat sans rien dire. C'est aussi ce qui garantit qu'aucun chemin
  // d'effacement ne passe par ici (E1).
  reglage_general_set: {
    ecran: "params",
    endpoint: "/api/beds24-mapping",
    action: "reglage_general_set",
    methode: "POST",
    args_autorises: ["reglage", "nouvelle"],
    args_valeurs: {
      // RECOPIÉES du catalogue serveur — et un test vérifie qu'elles lui sont
      // égales. Sans elles, le modèle compose du plausible (« nom_societe »
      // pour « societe_nom »), la carte s'affiche, et le clic échoue.
      reglage: [
        "societe_nom",
        "societe_adresse",
        "societe_email",
        "societe_tel",
        "mediateur_nom",
        "mediateur_coordonnees",
      ],
    },
    args_libres: ["nouvelle"],
    apercu: {
      lecture: "reglage_general_lire",
      depend_de: ["reglage"],
      jeton: "x-cfg-version",
    },
    titre: "Modifier un réglage général de la société",
    constatable:
      "la nouvelle valeur apparaît dans Paramètres › Général › Informations société — et c'est elle que les prochaines factures, les prochains contrats et les e-mails envoyés aux voyageurs emploient dès maintenant (les documents déjà émis, eux, gardent l'ancienne) ; pour le médiateur, l'article 16 du contrat ne s'affiche que lorsque le nom ET les coordonnées sont remplis",
    sujet: "reglages",
    cout: ["rien"],
    // ⚠️ RIEN NE PART AU CLIC, et c'est déjà pourquoi ce levier n'est pas
    // marqué `sortant`. Mais ce texte-là signera les prochains envois : la
    // destination dit où l'effet se voit, pas où le clic va.
    ou_ca_part: ["atteignable"],
    ne_fait_pas:
      "il n'envoie rien et n'émet aucune facture, et les documents DÉJÀ émis gardent l'ancienne valeur : seuls les prochains l'emploient. Il ne touche ni la TVA, ni l'IBAN, ni la numérotation — ces champs-là sont refusés au chargement.",
    dit_comme: [
      "Change l'adresse de ma société",
      "Corrige le téléphone qui apparaît sur mes factures",
      "Mets à jour le nom du médiateur",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
    // ⚠️ PAS DE MARQUAGE `sortant`, ET C'EST UNE DISTINCTION, PAS UN OUBLI. Le
    // clic n'envoie RIEN : aucun e-mail ne part, aucune facture n'est émise,
    // rien n'atteint Beds24. Ce que le réglage change, c'est le CONTENU des
    // envois futurs — et c'est le `constatable` qui le dit, en toutes lettres.
    // Poser `sortant` sur un geste qui n'envoie rien userait le marquage
    // exactement là où il doit compter.
    // ⚠️ Et pas de marquage `argent` non plus : la TVA et son taux — les deux
    // seuls champs de `cfg.co` qui décident d'un montant — sont hors catalogue,
    // refusés par la garde des mots.
    //
    // ⚠️ `sans_retour` : écrire ÉCRASE l'ancienne valeur, qui n'est stockée
    // nulle part. On peut reposer l'ancien texte ; on ne peut pas « annuler ».
    sans_retour:
      "L'ancienne valeur n'est gardée nulle part : pour revenir en arrière, il faudra la retaper.",
    // E1 ✔ (un texte, refaisable ; la carte montre l'ancienne valeur, et rien
    // ne se vide) · E2 ✔ (visible dans Paramètres juste après) · E3 ✔ (même
    // chemin que le bouton Enregistrer, diff intégral, jeton de version — et
    // écrivain unique de ce champ) · E4 ✔ (aucun fait attesté) · E5 ✔.
  },
});

module.exports = { LEVIERS_PARAMS };
